import type { SxProps, Theme } from '@mui/material'

import { getDialogStyles } from '../defaults'

const EXPANDED_DIALOG_Z_INDEX = 1400

/**
 * Canonical width of the centered session dialogs (conversation, most-recent
 * event, agent reply). Exposed as both the raw pixel count and the CSS string
 * so consumers that need to align with the dialog (e.g. the blocking inbox HUD)
 * share a single source of truth and cannot drift.
 */
export const DEFAULT_DIALOG_WIDTH_PX = 1200
export const DEFAULT_DIALOG_WIDTH = `${DEFAULT_DIALOG_WIDTH_PX}px`

export function createDialogPaperSx(theme: Theme): SxProps<Theme> {
  const styles = getDialogStyles(theme.palette.mode)
  return {
    backgroundColor: styles.backgroundColor,
    color: theme.palette.text.primary,
    borderRadius: styles.borderRadius,
    boxShadow: 'none',
  }
}

export function createDialogTitleSx(theme: Theme): SxProps<Theme> {
  const { titleBar } = getDialogStyles(theme.palette.mode)
  return {
    pt: titleBar.paddingTop,
    pb: titleBar.paddingBottom,
    borderBottom: `${titleBar.borderBottomWidth}px solid ${titleBar.borderBottomColor}`,
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    backgroundColor: titleBar.backgroundColor,
    zIndex: 1000,
  }
}

export function createDialogContentSx(theme: Theme): SxProps<Theme> {
  const { content } = getDialogStyles(theme.palette.mode)
  return {
    flex: 1,
    overflow: 'auto',
    pt: content.paddingTop,
    px: content.paddingHorizontal,
    pb: content.paddingBottom,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: content.backgroundColor,
  }
}

export function createDialogSx(
  theme: Theme,
  fullScreen: boolean,
  isExpanded: boolean,
  dialogWidth: string,
  dialogHeight: string,
): SxProps<Theme> {
  const styles = getDialogStyles(theme.palette.mode)
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...(isExpanded ? { zIndex: EXPANDED_DIALOG_Z_INDEX } : {}),
    '& .MuiDialog-paper': {
      borderRadius: fullScreen ? 0 : styles.borderRadius,
      minHeight: isExpanded ? '100vh' : dialogHeight,
      maxHeight: isExpanded ? '100vh' : dialogHeight,
      height: isExpanded ? '100vh' : dialogHeight,
      minWidth: isExpanded ? '100vw' : dialogWidth,
      maxWidth: isExpanded ? '100vw' : dialogWidth,
      width: isExpanded ? '100vw' : dialogWidth,
      m: fullScreen ? 0 : 2,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
  }
}
